import { jsonapiClient } from '@/api/client'
import type { components, paths } from '@/api/schema'
import { useApiErrorHandler } from '@/composables/useApiErrorHandler'
import { usePagination } from '@/composables/usePagination'
import { toast } from 'vue-sonner'
import { useI18n } from 'vue-i18n'
import { ref } from 'vue'
import type { Resource } from '@/api/resources'

type ContactsQueryParams = paths['/api/contacts']['get']['parameters']['query']
type ContactShowInclude = NonNullable<paths['/api/contacts/{id}']['get']['parameters']['query']>['include']
type Company = components['schemas']['CompanyResource']
type ContactRelationships = NonNullable<
  paths['/api/contacts']['post']['requestBody']['content']['application/vnd.api+json']['data']['relationships']
>

export function useContacts() {
  const { t } = useI18n()
  const { handleError } = useApiErrorHandler()

  // Map of company public_id → CompanyResource (populated from JSON:API includes)
  const includedCompanies = ref<Map<string, Company>>(new Map())

  const pagination = usePagination<Resource<'contact'>, ContactsQueryParams>(async (params) => {
    try {
      const mergedParams = { ...params, include: { company: true } }
      const response = await jsonapiClient.get('/api/contacts', { params: mergedParams })
      // Parse included company resources (present when include=company is requested)
      // Merged, not replaced: a later page's companies join the first page's
      // rather than erasing them.
      if (response.data.included) {
        const map = new Map<string, Company>(includedCompanies.value)
        for (const company of response.data.included) {
          map.set(company.id, company)
        }
        includedCompanies.value = map
      }
      return {
        data: response.data.data,
        meta: response.data.meta
      }
    } catch (error) {
      handleError(error)
      return null
    }
  })

  const fetchContact = async (id: string, include?: ContactShowInclude) => {
    try {
      const response = await jsonapiClient.get('/api/contacts/{id}', {
        path: { id },
        params: include ? { include } : undefined,
      })
      if (response.data.included) {
        const map = new Map<string, Company>(includedCompanies.value)
        for (const company of response.data.included) {
          map.set(company.id, company)
        }
        includedCompanies.value = map
      }
      return response.data.data
    } catch (error) {
      handleError(error)
      return null
    }
  }

  // `silent`: the caller owns a richer confirmation. Quick-capture creates the
  // contact, its first location and the job in one act — three stacked toasts
  // for one « Créer » is noise; the form toasts the job alone.
  const create = async (
    // ⚠ Keep this type COMPLETE: intake passes cell_phone (and the dedup
    // release verb) via spreads, which defeat excess-property checks — a key
    // missing here fails silently, never loudly.
    data: {
      first_name: string; last_name: string; email?: string; phone?: string
      cell_phone?: string; job_title?: string
      // « Nouveau propriétaire — le retirer de sa fiche » (writeOnly).
      release_conflicting_cell?: boolean
    },
    relationships?: ContactRelationships,
    options?: { silent?: boolean }
  ) => {
    try {
      const response = await jsonapiClient.post('/api/contacts', {
        data: { type: 'contact', attributes: data, relationships },
      })
      pagination.prependItem(response.data.data)
      if (!options?.silent) toast.success(t('contacts.createSuccess'))
      return response.data.data
    } catch (error) {
      handleError(error)
      return null
    }
  }

  // The allowlist exists to strip SERVER-COMPUTED attributes (created_at,
  // updated_at) when a caller passes a whole resource's attributes back.
  // ⚠ It is also a TRAP: it silently dropped `cell_phone` for two days after
  // the §102 column split — the profile form sent the key, this list ate it,
  // and the success toast honestly reported a request that no longer contained
  // the change. Every writable attribute MUST be listed here, and the guard is
  // `!== undefined` (not `!= null`): null is the CLEAR verb — for a phone slot
  // it is the release act — while undefined means « untouched ».
  // payment_first — the payer's « Paiement avant travaux » default.
  const WRITABLE_ATTRS = ['first_name', 'last_name', 'email', 'phone', 'cell_phone', 'job_title', 'payment_first'] as const

  const update = async (
    id: string,
    data: Partial<Resource<'contact'>['attributes']>,
    relationships?: ContactRelationships,
    opts: { silent?: boolean } = {}
  ) => {
    try {
      const cleanData: Record<string, string | boolean | null> = {}
      for (const key of WRITABLE_ATTRS) {
        const value = data[key]
        if (value !== undefined) cleanData[key] = value
      }

      const response = await jsonapiClient.patch('/api/contacts/{id}', {
        data: { id, type: 'contact', attributes: cleanData, relationships },
      }, { path: { id } })

      const updated = response.data.data
            pagination.replaceItem(id, updated)

      if (!opts.silent) toast.success(t('contacts.updateSuccess'))
      return updated
    } catch (error) {
      handleError(error)
      return null
    }
  }

  const remove = async (id: string) => {
    try {
      await jsonapiClient.delete('/api/contacts/{id}', { path: { id } })
      pagination.removeItem(id)
      return true
    } catch (error) {
      handleError(error)
      return false
    }
  }

  return {
    contacts: pagination.data,
    loading: pagination.loading,
    loadingMore: pagination.loadingMore,
    meta: pagination.meta,
    hasMore: pagination.hasMore,
    failed: pagination.failed,
    refetchContacts: pagination.refetch,
    fetchContacts: pagination.fetchPage,
    loadMoreContacts: pagination.loadMore,
    includedCompanies,
    fetchContact,
    create,
    update,
    remove
  }
}
